import {
  ForwardedRef,
  forwardRef,
  lazy,
  Ref,
  Suspense,
  useImperativeHandle
} from 'react';
import { ModelViewerElement } from '@google/model-viewer';

import { Units } from '../../types';

import { ModelViewerProvider } from './hooks/useModelViewer';
import { MeshEdgesMode } from '../../main/modelViewers/components/content/ModelViewerEditor/hooks/useModelViewerMeshEdges';
import {
  ModelViewerScreenshotHandle,
  ScreenshotOptions,
  useModelViewerScreenshot
} from './hooks/useModelViewerScreenshot';

import { ModelViewerRawProps } from './ModelViewerRaw';
import { ModelViewerDimensions } from './components/ModelViewerDimensions';
import { ModelViewerGridAxesHelper } from './components/ModelViewerGridAxesHelper';
import { ModelViewerMeshEdgesHelper } from './components/ModelViewerMeshEdgesHelper';

export type { ModelViewerScreenshotHandle, ScreenshotOptions };

export type ModelViewerProps = ModelViewerRawProps & {
  withDimensions?: boolean;
  withRuler?: boolean;
  units?: Units;
  withGridAxesHelper?: boolean;
  meshEdgesMode?: MeshEdgesMode;
  /**
   * Ref to the screenshot API. Exposes `captureScreenshot` (download to user)
   * and `getScreenshot` (returns result without downloading).
   *
   * @example
   * const screenshotRef = useRef<ModelViewerScreenshotHandle>(null);
   * // ...
   * await screenshotRef.current.captureScreenshot({ width: 1920, height: 1080 });
   * const { dataURL } = await screenshotRef.current.getScreenshot({ fov: 45 });
   */
  screenshotRef?: Ref<ModelViewerScreenshotHandle>;
};

const ModelViewerRaw = lazy(() => import('./ModelViewerRaw/ModelViewerRaw'));

function ModelViewerScreenshotBridge({
  screenshotRef
}: {
  screenshotRef: Ref<ModelViewerScreenshotHandle>;
}) {
  const { captureScreenshot, getScreenshot } = useModelViewerScreenshot();

  useImperativeHandle(
    screenshotRef,
    () => ({ captureScreenshot, getScreenshot }),
    [captureScreenshot, getScreenshot]
  );

  return null;
}

function ModelViewer(
  props: ModelViewerProps,
  ref: ForwardedRef<ModelViewerElement>
) {
  const {
    children,
    withDimensions,
    withRuler,
    units,
    withGridAxesHelper,
    meshEdgesMode,
    screenshotRef,
    ...otherProps
  } = props;

  return (
    <ModelViewerProvider>
      <Suspense>
        <ModelViewerRaw {...otherProps} ref={ref}>
          <>
            <ModelViewerDimensions
              withDimensions={withDimensions}
              withRuler={withRuler}
              units={units || Units.CM}
            />

            {withGridAxesHelper && <ModelViewerGridAxesHelper />}
            <ModelViewerMeshEdgesHelper mode={meshEdgesMode} />

            {children}

            <style>
              {`
              :not(:defined) > * {
                display: none;
              }

              [slot^="hotspot-"] {
                --min-hotspot-opacity: 1;
              }
            `}
            </style>
          </>
        </ModelViewerRaw>

        {screenshotRef && (
          <ModelViewerScreenshotBridge screenshotRef={screenshotRef} />
        )}
      </Suspense>
    </ModelViewerProvider>
  );
}

export default forwardRef(ModelViewer);
